import { useEffect, useState } from "react";
import { Sparkles, FileText, Save } from "lucide-react";

const TEXT = [["organizational_portrait", "Organizational portrait (at a glance)"], ["ground", "Ground · what the Portrait suggests"], ["ghosts", "Ghosts · what the Portrait suggests"], ["shifts", "Shifts · what the Portrait suggests"], ["signals", "Signals · what the Portrait suggests"], ["structural_considerations", "Structural considerations"]];
const LISTS = [["reclamation", "Reclamation (one per line)"], ["questions_for_leadership", "Questions for leadership (one per line)"]];
const THEMES = ["Strategic Direction", "Support & Opportunity", "Authority Under Pressure", "Creativity to Execution"];
const PATTERNS = ["Fear & Uncertainty × External Validation", "Future Orientation × Scaffolding", "Joy, Freedom & Play × Intentional Practice", "Relationship with Self × External Validation × Scaffolding"];
const IMPL = ["Culture", "Innovation", "Strategy & Execution", "People & Talent", "Decision-Making & Governance", "Human Authority & AI", "Succession & Leadership Continuity", "Organizational Risk"];
const RISKS = ["Decision Bottlenecks", "Consensus Without Challenge", "Innovation Attrition", "Strategy-Execution Drift", "AI Over-Deference", "Uneven Employee Experience"];
const GSS = [["ground", "Ground"], ["ghosts", "Ghosts"], ["shifts", "Shifts"], ["signals", "Signals"]];
const SECTIONS = ["Creativity & Innovation", "Strategic Planning & Execution", "People, Talent & HR", "Succession Planning & Leadership Continuity", "Decision-Making & Governance", "Human Authority & AI"];
const DIMS = [["self_relationship", "Relationship with Self"], ["external_validation", "Use of External Validation"], ["fear_uncertainty", "Fear & Uncertainty"], ["scaffolding", "Scaffolding"], ["intentional_practice", "Intentional Practice"], ["future_orientation", "Future Orientation"], ["joy_freedom_play", "Joy, Freedom & Play"]];
const blankItem = () => ({ title: "", dimensions: [], text: "", question: "" });
const four = (xs) => [0, 1, 2, 3].map((i) => ({ ...blankItem(), ...((xs || [])[i] || {}) }));
const FIELD = "w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-3 py-2 text-sm text-[#E8EAEE] leading-relaxed focus:border-brass outline-none";
const BTN = "inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full cg-scale-btn disabled:opacity-60";

const toDraft = (c = {}) => {
  const cul = c.culture || {};
  const theme = Object.fromEntries((cul.culture_at_a_glance || []).map((t) => [t.theme, t.text]));
  const note = Object.fromEntries((cul.connections || []).map((p) => [Number(p.pattern), p.note]));
  return {
    ...Object.fromEntries(TEXT.map(([k]) => [k, c[k] || ""])),
    ...Object.fromEntries(LISTS.map(([k]) => [k, (c[k] || []).join("\n")])),
    c3_actions: (c.c3_actions || []).map((a) => ({ focus: a.focus || "", clarify: a.clarify || "", choose: a.choose || "", condition: a.condition || "" })),
    themes: THEMES.map((t) => theme[t] || ""), notes: PATTERNS.map((_, i) => note[i + 1] || ""),
    legacy: !cul.glance_cards && Boolean(cul.culture_at_a_glance),
    glance_intro: cul.glance_intro || "", tension: cul.tension || "", connections_intro: cul.connections_intro || "",
    glance_cards: four(cul.glance_cards), connection_patterns: four(cul.connection_patterns),
    impl: IMPL.map((a) => (c.business_implications || {})[a] || ""),
    risks: RISKS.map((r) => (c.risks || {})[r] || ""),
    sections: SECTIONS.map((b) => (c.business_sections || {})[b] || ""),
    gss: Object.fromEntries(GSS.map(([k]) => [k, { culture: c.gss_culture?.[k]?.culture || "", explore: c.gss_culture?.[k]?.explore || "" }])),
  };
};

const fromDraft = (d) => ({
  ...Object.fromEntries(TEXT.map(([k]) => [k, d[k]])),
  ...Object.fromEntries(LISTS.map(([k]) => [k, d[k].split("\n").map((x) => x.trim()).filter(Boolean)])),
  c3_actions: d.c3_actions,
  business_implications: Object.fromEntries(IMPL.map((a, i) => [a, d.impl[i]])),
  risks: Object.fromEntries(RISKS.map((r, i) => [r, d.risks[i]])),
  business_sections: Object.fromEntries(SECTIONS.map((b, i) => [b, d.sections[i]])),
  gss_culture: d.gss,
  culture: d.legacy
    ? { culture_at_a_glance: THEMES.map((theme, i) => ({ theme, text: d.themes[i] })), connections: PATTERNS.map((_, i) => ({ pattern: i + 1, note: d.notes[i] })) }
    : { glance_intro: d.glance_intro, glance_cards: d.glance_cards, tension: d.tension, connections_intro: d.connections_intro, connection_patterns: d.connection_patterns },
});

const Area = ({ label, value, onChange, rows = 4, testId }) => (
  <label className="block">
    <span className="cg-eyebrow block mb-1.5">{label}</span>
    <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={FIELD} />
  </label>
);

const Group = ({ title, children }) => (
  <div className="border-t border-[var(--cg-border)] pt-5">
    <p className="font-serif text-xl text-[#F3F4F6] mb-3">{title}</p>
    <div className="grid md:grid-cols-2 gap-4">{children}</div>
  </div>
);

const DimPicker = ({ value, onChange, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {DIMS.map(([k, label]) => {
      const on = value.includes(k);
      return (
        <button key={k} type="button" data-testid={`${testId}-${k}`} aria-pressed={on}
          onClick={() => onChange(on ? value.filter((x) => x !== k) : [...value, k].slice(0, 3))}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "border-brass bg-brass/15 text-brass" : "border-[var(--cg-border)] text-[#9CA3AF] hover:border-brass"}`}>{label}</button>
      );
    })}
  </div>
);

const ItemEditor = ({ item, label, qLabel, onChange, testId }) => (
  <div className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg p-3 space-y-3" data-testid={testId}>
    <p className="cg-eyebrow">{label}</p>
    <input value={item.title} onChange={(e) => onChange({ ...item, title: e.target.value })} placeholder="Title" data-testid={`${testId}-title`} className={FIELD} />
    <div><span className="text-xs text-[#6B7280] block mb-1">Dimensions shown with this organization's results (up to 3)</span>
      <DimPicker value={item.dimensions} onChange={(v) => onChange({ ...item, dimensions: v })} testId={`${testId}-dims`} /></div>
    <Area label="Interpretation (new line between paragraphs)" value={item.text} rows={5} onChange={(v) => onChange({ ...item, text: v })} testId={`${testId}-text`} />
    <Area label={qLabel} value={item.question} rows={2} onChange={(v) => onChange({ ...item, question: v })} testId={`${testId}-question`} />
  </div>
);

const CultureEditor = ({ d, setD, prefix }) => {
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const setItem = (k, i, v) => setD((x) => ({ ...x, [k]: x[k].map((y, j) => (j === i ? v : y)) }));
  return (
    <>
      <div className="border-t border-[var(--cg-border)] pt-5 space-y-4">
        <p className="font-serif text-xl text-[#F3F4F6]">Your Culture at a Glance</p>
        <Area label="Introduction (new line between paragraphs)" value={d.glance_intro} rows={5} onChange={(v) => set("glance_intro", v)} testId={`${prefix}-edit-glance-intro`} />
        <div className="grid md:grid-cols-2 gap-4">{d.glance_cards.map((it, i) => <ItemEditor key={i} item={it} label={`Card ${i + 1}`} qLabel="Cultural question" onChange={(v) => setItem("glance_cards", i, v)} testId={`${prefix}-edit-card-${i + 1}`} />)}</div>
        <Area label="The cultural tension" value={d.tension} rows={3} onChange={(v) => set("tension", v)} testId={`${prefix}-edit-tension`} />
      </div>
      <div className="border-t border-[var(--cg-border)] pt-5 space-y-4">
        <p className="font-serif text-xl text-[#F3F4F6]">Culture Lives in the Connections</p>
        <Area label="Introduction" value={d.connections_intro} rows={3} onChange={(v) => set("connections_intro", v)} testId={`${prefix}-edit-connections-intro`} />
        <div className="grid md:grid-cols-2 gap-4">{d.connection_patterns.map((it, i) => <ItemEditor key={i} item={it} label={`Pattern ${i + 1}`} qLabel="Leadership question" onChange={(v) => setItem("connection_patterns", i, v)} testId={`${prefix}-edit-conn-${i + 1}`} />)}</div>
      </div>
    </>
  );
};

// Super Admin only: generate, edit and download the organizational report. Never shown to organization admins.
export const ReportEditor = ({ interp, onGenerate, genBusy, onSave, onPdf, n, prefix }) => {
  const [d, setD] = useState(() => toDraft(interp?.content));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setD(toDraft(interp?.content)); }, [interp]);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const setIdx = (k, i, v) => setD((x) => ({ ...x, [k]: x[k].map((y, j) => (j === i ? v : y)) }));
  const save = async () => { setSaving(true); try { await onSave(fromDraft(d)); } finally { setSaving(false); } };

  return (
    <div data-testid={`${prefix}-report-editor`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-2xl text-brass">Organizational report · Super Admin draft</h3>
          <p className="text-xs text-[#6B7280] mt-1">Only you can see, edit and download this. Organization admins do not receive it in the app.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={onGenerate} disabled={genBusy} data-testid={`${prefix}-generate`} className={`${BTN} bg-brass text-obsidian font-medium hover:bg-amber2`}>
            <Sparkles className="w-4 h-4" /> {genBusy ? "Generating…" : interp ? "Regenerate draft" : "Generate draft"}
          </button>
          {interp && <button onClick={save} disabled={saving} data-testid={`${prefix}-save`} className={`${BTN} border border-brass text-brass`}><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save edits"}</button>}
          {interp && <button onClick={onPdf} data-testid={`${prefix}-pdf`} className={`${BTN} border border-[rgba(255,255,255,0.15)] hover:border-brass`}><FileText className="w-4 h-4" /> Download PDF</button>}
        </div>
      </div>
      {!interp ? (
        <p className="text-sm text-[#9CA3AF]">No draft yet. Generate one from {n} aggregated responses (individual portraits are never exposed). Regenerating replaces any edits.</p>
      ) : (
        <div className="space-y-5">
          {TEXT.map(([k, l]) => <Area key={k} label={l} value={d[k]} onChange={(v) => set(k, v)} rows={k === "organizational_portrait" ? 7 : 4} testId={`${prefix}-edit-${k}`} />)}
          <Group title="Ground, Ghosts, Shifts & Signals · culture and leadership">
            {GSS.map(([k, l]) => ["culture", "explore"].map((f) => (
              <Area key={`${k}-${f}`} label={`${l} · ${f === "culture" ? "what this may reveal about culture" : "what leadership should explore"}`} value={d.gss[k][f]} rows={5} testId={`${prefix}-edit-${k}-${f}`}
                onChange={(v) => setD((x) => ({ ...x, gss: { ...x.gss, [k]: { ...x.gss[k], [f]: v } } }))} />
            )))}
          </Group>
          <Group title="Business Implications at a Glance">{IMPL.map((a, i) => <Area key={a} label={a} value={d.impl[i]} onChange={(v) => setIdx("impl", i, v)} rows={5} testId={`${prefix}-edit-impl-${i + 1}`} />)}</Group>
          <Group title="Detailed business sections">{SECTIONS.map((b, i) => <Area key={b} label={b} value={d.sections[i]} onChange={(v) => setIdx("sections", i, v)} rows={7} testId={`${prefix}-edit-section-${i + 1}`} />)}</Group>
          <Group title="Potential Organizational Risks">{RISKS.map((r, i) => <Area key={r} label={r} value={d.risks[i]} onChange={(v) => setIdx("risks", i, v)} rows={5} testId={`${prefix}-edit-risk-${i + 1}`} />)}</Group>
          {d.legacy && <p className="text-sm text-brass border border-brass/40 rounded-lg p-3" data-testid={`${prefix}-culture-legacy`}>This draft uses the earlier culture pages. Regenerate the draft to get the results-based Culture at a Glance and Connections pages (regenerating replaces edits).</p>}
          {!d.legacy && <CultureEditor d={d} setD={setD} prefix={prefix} />}
          {d.legacy && <div className="border-t border-[var(--cg-border)] pt-5">
            <p className="font-serif text-xl text-[#F3F4F6] mb-3">Your Culture at a Glance</p>
            <div className="grid md:grid-cols-2 gap-4">{THEMES.map((t, i) => <Area key={t} label={t} value={d.themes[i]} onChange={(v) => setIdx("themes", i, v)} rows={3} testId={`${prefix}-edit-theme-${i + 1}`} />)}</div>
          </div>}
          {d.legacy && <div className="border-t border-[var(--cg-border)] pt-5">
            <p className="font-serif text-xl text-[#F3F4F6] mb-3">Culture Lives in the Connections</p>
            <div className="grid md:grid-cols-2 gap-4">{PATTERNS.map((t, i) => <Area key={t} label={`Pattern ${i + 1} · ${t}`} value={d.notes[i]} onChange={(v) => setIdx("notes", i, v)} rows={3} testId={`${prefix}-edit-pattern-${i + 1}`} />)}</div>
          </div>}
          {LISTS.map(([k, l]) => <Area key={k} label={l} value={d[k]} onChange={(v) => set(k, v)} rows={5} testId={`${prefix}-edit-${k}`} />)}
          {d.c3_actions.map((a, i) => (
            <div key={i} className="bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg p-3 grid md:grid-cols-2 gap-3">
              {["focus", "clarify", "choose", "condition"].map((f) => (
                <Area key={f} label={`C³ ${i + 1} · ${f}`} value={a[f]} rows={2} testId={`${prefix}-edit-c3-${i + 1}-${f}`}
                  onChange={(v) => setD((x) => ({ ...x, c3_actions: x.c3_actions.map((y, j) => (j === i ? { ...y, [f]: v } : y)) }))} />
              ))}
            </div>
          ))}
          <p className="text-xs text-[#6B7280] font-mono">Model {interp.ai_model} · prompt {interp.prompt_version}{interp.edited_at && ` · edited ${new Date(interp.edited_at).toLocaleString()}`}</p>
        </div>
      )}
    </div>
  );
};

// Drafting runs in the background on the server; wait until it finishes.
export const waitForDraft = async (get) => {
  let misses = 0;
  for (let i = 0; i < 75; i += 1) {
    await new Promise((r) => setTimeout(r, 4000));
    let r;
    try { r = await get(); misses = 0; } catch (e) {
      misses += 1;
      if (misses > 5) throw e;
      continue;
    }
    const st = r.data.job?.status;
    if (st === "done") return r.data.interpretation;
    if (st === "failed") throw new Error(r.data.job.error || "Drafting failed");
  }
  throw new Error("Drafting is taking longer than expected. Refresh in a minute.");
};

export const ReportNotice = () => (
  <div className="cg-card p-7" data-testid="org-report-notice">
    <h3 className="font-serif text-2xl text-brass">Organizational Authority Portrait</h3>
    <p className="text-sm text-[#B9BDC7] mt-2 leading-relaxed">Your Organizational Authority Portrait is prepared, reviewed and shared with you by Caesar's Ghost once enough participants have completed the assessment.</p>
  </div>
);
